import { defineProperty as _defineProperty, initializerDefineProperty as _initializerDefineProperty, applyDecoratedDescriptor as _applyDecoratedDescriptor } from '../../_virtual/_rollupPluginBabelHelpers.js';
import axios from 'axios';
import { makeAutoObservable, observable } from 'mobx';
import { configRefs } from '@utils/constants';

var _class, _descriptor, _descriptor2;
/**
 * Contains data about a single user's favorites and progressions among books
 * within a shelf.
 */
let UserDataModel = (_class = class UserDataModel {
  /** Apply data and subscribe to filter changes when the model is created */
  constructor(data) {
    _defineProperty(this, "relatedShelfId", void 0);
    /**
     * Contains a collection of books currently marked as favorites (we're using
     * a map because mobx doesn't support sets).
     */
    _initializerDefineProperty(this, "favorites", _descriptor, this);
    /**
     * Contains a collection of progression indications for all books available
     * within the library.
     */
    _initializerDefineProperty(this, "progression", _descriptor2, this);
    /**
     * Contains an internal reference back to the shelf that favorites and
     * progressions are handled for.
     */
    _defineProperty(this, "shelf", void 0);
    /**
     * Contains the id of the product that the given bookshelf is a part of (we
     * need this information when managing user favorites).
     */
    _defineProperty(this, "productId", void 0);
    /**
     * Contains the ID of the user that favorites and progressions are handled
     * for.
     */
    _defineProperty(this, "userId", void 0);
    /**
     * Contains the ID of the institution that the user is associated with.
     */
    _defineProperty(this, "userInstitutionId", void 0);
    Object.assign(this, data);
    this.relatedShelfId = data.shelf.id;
    makeAutoObservable(this);
  }

  /**
   * Helper that fetches the content of a shelf based on it's id, possibly
   * also fetching associated user data (such as favorites and progression in
   * the books).
   */
  static async FETCH(userId, userInstitutionId, productId, shelf) {
    // Find the list of user favorites, and make sure that the related books
    // are properly marked
    const favoritesResponse = await axios.get(`${await configRefs.apis.userdata.get()}/api/Favorites?userID=${userId}&productID=${productId}`);

    // Loop through and mark favorites properly
    const favorites = new Map();
    for (const favorite of favoritesResponse.data) {
      favorites.set(favorite.learningObject.id, true);
    }

    // Loop through all books and fetch progression status
    const progression = new Map();

    // Get progression for the book from the remote endpoint
    const progressionResponse = await axios.get(`${await configRefs.apis.userdata.get()}/api/Progressions?userID=${userId}`);
    for (const progressionData of progressionResponse.data) {
      const bookId = cleanUpId(progressionData.learningObject.id);
      progression.set(bookId, {
        lastRead: progressionData.modified,
        progression: Math.max(0, Math.min(1, progressionData.completion / 100))
      });
    }

    // Create model
    return new UserDataModel({
      favorites,
      productId,
      progression,
      shelf,
      userId,
      userInstitutionId
    });
  }

  /** Helper that determines if the user has marked this item as a favorite */
  isFavorite(item) {
    return this.favorites.get(item.id) === true;
  }

  /** Helper that returns the full list of books favoritted by the user */
  getFavorites() {
    return Array.from(this.favorites.keys()).map(itemId => this.shelf.getItemById(itemId)).filter(item => item !== undefined);
  }

  /** Helper that toggles favorite status on/off for the specified item */
  async toggleFavorite(item) {
    // Should we mark an item as favorite?
    if (this.favorites.get(item.id) !== true) {
      // Perform the request
      await axios.post(`${await configRefs.apis.userdata.get()}/api/Favorites`, {
        learningObject: {
          id: item.id,
          referenceID: "",
          title: item.title,
          type: item.getTypeString()
        },
        productId: this.productId,
        userID: this.userId,
        userInstitutionID: this.userInstitutionId
      });

      // Update internal state
      this.favorites.set(item.id, true);

      // ... Or should we delete the favorite?
    } else {
      // Delete the favorite
      await axios.delete(`${await configRefs.apis.userdata.get()}/api/Favorites?userID=${this.userId}&learningObjectID=${item.id}`);

      // Update internal state
      this.favorites.delete(item.id);
    }
  }

  /**
   * Helper that returns the user's progression in the book on a scale from 0
   * to 1.
   */
  getProgression(book) {
    const progression = this.progression.get(book.isbn);
    return progression ? progression.progression : 0;
  }

  /** Determines if the user has read any books within the shelf yet or not */
  hasReadBooks() {
    return this.progression.size > 0;
  }

  /** Returns a list of books inside which the user has had progression */
  getLastReadBooks() {
    // Sort progressions by timestamp of when the user was last reading in
    // them
    const progressions = Array.from(this.progression.entries());
    const readBooks = [];
    for (const [isbn, progression] of progressions) {
      readBooks.push({
        isbn,
        lastRead: progression.lastRead
      });
    }

    // Sort books by last read timestamp and resolve ids to BookModel
    return readBooks.sort((a, b) => b.lastRead - a.lastRead).map(book => this.shelf.getBookByIsbn(book.isbn)).filter(book => !!book);
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "favorites", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return new Map();
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "progression", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return new Map();
  }
}), _class);
function cleanUpId(id) {
  return id.replace(/^\{/, "").replace(/\}$/, "").toLowerCase();
}

export { UserDataModel };
//# sourceMappingURL=UserDataModel.js.map
